import React, { useState, useEffect } from 'react';
import { 
  Tag, 
  Clock, 
  MapPin, 
  Percent,
  Gift,
  Ticket,
  ChevronRight,
  Calendar,
  Star,
  Store,
  X,
  Check,
  Loader2
} from 'lucide-react';
import { campaignsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const CampaignsPage = ({ onTabChange }) => {
  const { isAuthenticated } = useAuth();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('active');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [usedCampaigns, setUsedCampaigns] = useState([]);

  // Dark mode classes - Fixed colors
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-[#f5f7fa]';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const data = await campaignsApi.getAll();
        // Defensive: Ensure data is array
        setCampaigns(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  // Defensive Filtering: Ensure campaigns is an array before filtering
  const safeCampaigns = Array.isArray(campaigns) ? campaigns : [];
  const activeCampaigns = safeCampaigns.filter(c => c.isActive);
  const expiredCampaigns = safeCampaigns.filter(c => !c.isActive);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'discount': return Percent;
      case 'bundle': return Gift;
      case 'free': return Ticket;
      case 'event': return Calendar;
      default: return Tag;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'discount': return 'bg-red-500';
      case 'bundle': return 'bg-purple-500';
      case 'free': return 'bg-green-500';
      case 'event': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const displayCampaigns = activeTab === 'active' ? activeCampaigns : expiredCampaigns;

  const handleUseCampaign = async (campaign) => {
    if (!isAuthenticated) {
      alert('Kampanyayı kullanmak için giriş yapmalısınız');
      return;
    }

    try {
      await campaignsApi.use(campaign.id);
      setUsedCampaigns([...usedCampaigns, campaign.id]);
      setSelectedCampaign(null);
    } catch (error) {
      console.error('Error using campaign:', error);
    }
  };

  const handleViewBusiness = (businessId) => {
    if (onTabChange && businessId) {
      onTabChange('businesses');
    }
    setSelectedCampaign(null);
  };

  // Loading State
  if (loading) {
    return (
      <div className={`h-full ${bgMain} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  // Campaign Detail Modal
  if (selectedCampaign) {
    const TypeIcon = getTypeIcon(selectedCampaign.type);
    const isUsed = usedCampaigns.includes(selectedCampaign.id);

    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        {/* Header Image */}
        <div className="relative h-56">
          <img 
            src={selectedCampaign.image} 
            alt={selectedCampaign.title} 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://images.unsplash.com/photo-1607083206968-13611e3d76db?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <button
            onClick={() => setSelectedCampaign(null)}
            className={`absolute top-4 left-4 w-10 h-10 ${darkMode ? "bg-slate-800/90" : "bg-white/90"} rounded-full flex items-center justify-center`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-white' : 'text-gray-900'}`} />
          </button>
          <div className={`absolute top-4 right-4 px-3 py-1 ${getTypeColor(selectedCampaign.type)} text-white text-sm font-bold rounded-full flex items-center gap-1`}>
            <TypeIcon className="w-4 h-4" />
            {selectedCampaign.discount}
          </div>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h1 className="text-2xl font-bold mb-1">{selectedCampaign.title}</h1>
            <p className="text-white/80">{selectedCampaign.businessName}</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto p-4">
          {/* Description */}
          <div className={`${bgCard} rounded-2xl p-4 shadow-sm mb-4`}>
            <p className={`${textPrimary} mb-4`}>{selectedCampaign.description}</p>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Clock className={`w-5 h-5 ${textSecondary}`} />
                <span className={textPrimary}>Son kullanım: {selectedCampaign.validUntil}</span>
              </div>
              <div className="flex items-center gap-3">
                <Star className={`w-5 h-5 ${textSecondary}`} />
                <span className={textPrimary}>{selectedCampaign.usedCount} kez kullanıldı</span>
              </div>
            </div>
          </div>

          {/* Business Link */}
          {selectedCampaign.businessId && (
            <button
              onClick={() => handleViewBusiness(selectedCampaign.businessId)}
              className={`w-full ${bgCard} rounded-2xl p-4 shadow-sm mb-4 flex items-center gap-3 hover:bg-gray-50 transition-colors`}
            >
              <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center">
                <Store className="w-6 h-6 text-[#1e3a5f]" />
              </div>
              <div className="flex-1 text-left">
                <h3 className={`font-semibold ${textPrimary}`}>{selectedCampaign.businessName}</h3>
                <p className={`text-sm ${textSecondary}`}>İşletme detaylarını görüntüle</p>
              </div>
              <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
            </button>
          )}

          {/* Use Campaign Button */}
          {selectedCampaign.isActive && (
            <button
              onClick={() => handleUseCampaign(selectedCampaign)}
              disabled={isUsed}
              className={`w-full py-4 rounded-xl font-medium flex items-center justify-center gap-2 ${
                isUsed 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-[#1e3a5f] text-white hover:bg-[#2a4a73]'
              } transition-colors`}
            >
              {isUsed ? (
                <>
                  <Check className="w-5 h-5" />
                  Kampanya Kullanıldı
                </>
              ) : (
                <>
                  <Tag className="w-5 h-5" />
                  Kampanyayı Kullan
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full ${bgMain} overflow-auto pb-20`}>
      <div className="max-w-3xl mx-auto p-4">
        <h1 className={`text-2xl font-bold ${textPrimary} mb-2`}>Kampanyalar</h1>
        <p className={`${textSecondary} mb-4`}>Özel fırsatları kaçırmayın!</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'active'
                ? 'bg-[#1e3a5f] text-white'
                : `bg-white text-gray-600 border ${borderColor}`
            }`}
          >
            Aktif ({activeCampaigns.length})
          </button>
          <button
            onClick={() => setActiveTab('expired')}
            className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'expired'
                ? 'bg-[#1e3a5f] text-white'
                : `bg-white text-gray-600 border ${borderColor}`
            }`}
          >
            Sona Eren ({expiredCampaigns.length})
          </button>
        </div>

        {/* Campaign List */}
        <div className="space-y-4">
          {Array.isArray(displayCampaigns) && displayCampaigns.map((campaign) => {
            const TypeIcon = getTypeIcon(campaign.type);
            const isUsed = usedCampaigns.includes(campaign.id);
            
            return (
              <div
                key={campaign.id}
                onClick={() => setSelectedCampaign(campaign)}
                className={`${bgCard} rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                  !campaign.isActive ? 'opacity-60' : ''
                }`}
              >
                <div className="relative h-40">
                  <img
                    src={campaign.image}
                    alt={campaign.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://images.unsplash.com/photo-1607083206968-13611e3d76db?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className={`absolute top-3 left-3 px-3 py-1 ${getTypeColor(campaign.type)} text-white text-sm font-bold rounded-full flex items-center gap-1`}>
                    <TypeIcon className="w-4 h-4" />
                    {campaign.discount}
                  </div>
                  {isUsed && (
                    <div className="absolute top-3 right-3 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Kullanıldı
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="font-bold text-white text-lg mb-1">{campaign.title}</h3>
                    <p className="text-white/80 text-sm">{campaign.businessName}</p>
                  </div>
                </div>
                <div className="p-4">
                  <p className={`text-sm ${textSecondary} mb-3`}>{campaign.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <div className={`flex items-center gap-1 ${textSecondary}`}>
                      <Clock className="w-4 h-4" />
                      <span>{campaign.validUntil}</span>
                    </div>
                    <div className={`flex items-center gap-1 ${textSecondary}`}>
                      <Star className="w-4 h-4" />
                      <span>{campaign.usedCount} kez kullanıldı</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {Array.isArray(displayCampaigns) && displayCampaigns.length === 0 && !loading && (
          <div className="text-center py-12">
            <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className={textSecondary}>
              {activeTab === 'active' ? 'Aktif kampanya yok' : 'Sona eren kampanya yok'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignsPage;